import React, { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const FAQSection = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // Premier item ouvert par défaut

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  // FAQ selon les spécifications utilisateur - accordéon
  const faqItems = [
    {
      question: "Quels sont les horaires d'arrivée et de départ ?",
      answer: "L'arrivée se fait à partir de 16h et le départ avant 11h. Des horaires flexibles peuvent être arrangés selon vos besoins, n'hésitez pas à nous contacter."
    },
    {
      question: "Les draps et serviettes sont-ils fournis ?",
      answer: "Les draps sont inclus et les lits sont faits à votre arrivée. Les serviettes ne sont pas incluses mais disponibles en option à 6 €/personne pour l'ensemble du séjour (2 serviettes par personne)."
    },
    {
      question: "Y a-t-il un parking privé sur place ?",
      answer: "Oui, le stationnement est gratuit et disponible à proximité immédiate du gîte. Pas de souci de parking même avec plusieurs véhicules."
    },
    {
      question: "Les animaux sont-ils acceptés ?",
      answer: "Oui, nous acceptons les animaux de compagnie sans supplément. Votre compagnon à quatre pattes sera le bienvenu dans notre gîte et pourra profiter du jardin clôturé."
    },
    {
      question: "Comment se déroule la réservation ?",
      answer: "Vous pouvez réserver directement via Airbnb, Booking.com ou nous contacter par email/téléphone. Un acompte de 30% est demandé à la réservation, le solde étant dû à l'arrivée."
    },
    {
      question: "Le gîte est-il adapté aux enfants ?",
      answer: "Absolument ! Le gîte dispose d'un terrain de pétanque, d'une table de ping-pong, d'un hamac et d'un grand jardin sécurisé. Lit bébé et chaise haute disponibles sur demande."
    },
    {
      question: "Quelles sont les activités à proximité ?",
      answer: "Clécy offre de nombreuses activités : randonnées, canoë-kayak sur l'Orne, via ferrata, équitation, pêche, et découverte des paysages de Suisse Normande. Tout est accessible à pied ou en quelques minutes en voiture."
    },
    {
      question: "Y a-t-il le Wi-Fi et la TV ?",
      answer: "Oui, le Wi-Fi haut débit est gratuit et disponible dans tout le gîte. Une télévision est présente dans le salon pour vos moments de détente."
    },
    {
      question: "Quel est le montant du ménage ? Est-il obligatoire ?",
      answer: "Le ménage de fin de séjour est obligatoire et facturé 70 €. Il comprend le nettoyage complet du gîte, changement des draps et préparation pour les prochains hôtes."
    },
    {
      question: "Que se passe-t-il en cas d'annulation ?",
      answer: "Les conditions d'annulation varient selon la plateforme de réservation (Airbnb, Booking) ou nos conditions générales pour les réservations directes. Nous restons flexibles selon les circonstances."
    }
  ];

  return (
    <section 
      id="faq" 
      className="section-padding bg-white"
      aria-labelledby="faq-title"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-12 md:mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#1C3A27]/10 rounded-full flex items-center justify-center">
              <HelpCircle size={32} className="text-[#1C3A27]" />
            </div>
          </div>
          <h2 
            id="faq-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            FAQ Pratique
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Toutes les réponses aux questions les plus fréquentes sur votre séjour
          </p>
        </div>

        {/* Accordéon FAQ */}
        <div className="space-y-4">
          {faqItems.map((item, index) => {
            const isOpen = openItems.has(index);
            return (
              <div
                key={index}
                className="bg-white rounded-xl border border-[#EDE6DB]/50 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2 rounded-xl"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="font-semibold text-[#1C3A27] text-base md:text-lg pr-4">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {isOpen ? (
                      <ChevronUp size={20} className="text-[#1C3A27]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#1C3A27]" />
                    )}
                  </div>
                </button>
                
                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 pb-5">
                    <div className="border-t border-[#EDE6DB]/30 pt-4">
                      <p className="text-[#1C3A27]/80 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact pour questions supplémentaires */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="bg-[#EDE6DB]/30 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#1C3A27] mb-4">
              Une autre question ?
            </h3>
            <p className="text-[#1C3A27]/70 mb-6 leading-relaxed">
              N'hésitez pas à nous contacter directement. Nathalie & Wilhelm se feront un plaisir de vous renseigner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:wilhelm.martel@orange.fr"
                className="btn-primary px-6 py-3 hover:bg-[#2A5236] transition-colors"
                aria-label="Nous contacter par email"
              >
                ✉️ Nous écrire
              </a>
              <a
                href="tel:+33664104815"
                className="btn-secondary px-6 py-3 hover:bg-[#1C3A27] hover:text-white transition-colors"
                aria-label="Nous appeler"
              >
                📞 Nous appeler
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;